import React from 'react';
import { Link } from 'react-router-dom';
import BlueCallToAction from '../../components/common/BlueCallToAction';

/**
 * Claims Management Solution Page
 * End-user focused view of Videxa's claims management capabilities
 */
const ClaimsManagementPage = () => {
  const claimsWorkflow = [
    {
      step: "1",
      title: "Claim Creation",
      description: "Automatically generate claims from encounter data with proper coding and documentation.",
      features: ["Auto-population from EHR", "CDM-based charging", "Documentation validation"]
    },
    {
      step: "2",
      title: "Claim Scrubbing",
      description: "AI-powered review catches errors before submission to maximize first-pass acceptance.",
      features: ["Edit checking", "Compliance validation", "Missing data alerts"]
    },
    {
      step: "3",
      title: "Submission",
      description: "Electronic submission to all major payers with real-time tracking.",
      features: ["Multi-payer support", "Batch processing", "Status tracking"]
    },
    {
      step: "4",
      title: "Follow-Up",
      description: "Automated work queues prioritize actions for fastest resolution.",
      features: ["Smart prioritization", "Aging management", "Appeal automation"]
    }
  ];

  const features = [
    {
      title: "Intelligent Claim Editing",
      description: "AI-powered claim scrubbing identifies and resolves errors before submission, dramatically improving clean claim rates.",
      icon: "🤖"
    },
    {
      title: "Multi-Payer Support",
      description: "Submit claims to all commercial payers, Medicare, Medicaid, and workers' comp through a single interface.",
      icon: "🏥"
    },
    {
      title: "Real-Time Eligibility",
      description: "Verify patient coverage and benefits instantly to prevent claim rejections at the front end.",
      icon: "✓"
    },
    {
      title: "Automated Follow-Up",
      description: "Intelligent work queues automatically prioritize claims requiring action based on age, value, and probability of payment.",
      icon: "📋"
    },
    {
      title: "Denial Prevention",
      description: "Predictive analytics identify claims at risk of denial before submission, allowing proactive intervention.",
      icon: "🛡️"
    },
    {
      title: "Payment Posting",
      description: "Automated ERA/EOB processing and payment reconciliation with exception handling.",
      icon: "💰"
    }
  ];

  const metrics = [
    { value: "94%", label: "Clean Claim Rate", description: "Industry average: 75-85%" },
    { value: "3 Days", label: "Average Time to Payment", description: "Down from 14+ days" },
    { value: "65%", label: "Denial Rate Reduction", description: "Fewer claims to rework" },
    { value: "40%", label: "Staff Time Saved", description: "On claims processing" }
  ];

  const painPoints = [
    {
      problem: "Manual Data Entry",
      impact: "Errors, delays, and staff burnout",
      solution: "Automated claim generation from EHR data"
    },
    {
      problem: "Payer Rules Complexity",
      impact: "Rejected claims and compliance issues",
      solution: "Built-in payer-specific edit rules that update automatically"
    },
    {
      problem: "Tracking Challenges",
      impact: "Lost revenue from forgotten follow-ups",
      solution: "Real-time dashboards and automated work queues"
    },
    {
      problem: "Appeal Bottlenecks",
      impact: "Missed deadlines and write-offs",
      solution: "Templated appeals with deadline tracking"
    }
  ];

  return (
    <div className="claims-management-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              Claims <span style={{ color: '#FFC000' }}>Management</span>
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-gray-700">
              Submit clean claims. Get paid faster. Reduce denials.
            </p>
            <p className="text-lg mb-8 text-gray-600">
              Videxa's intelligent claims management platform streamlines your entire claims lifecycle from creation to payment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/cdi-trial"
                style={{
                  backgroundColor: '#0E3056',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600'
                }}
                className="hover:opacity-90 transition-opacity"
              >
                Start Free Trial
              </Link>
              <Link
                to="/contact"
                style={{
                  border: '2px solid #0E3056',
                  color: '#0E3056',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600'
                }}
                className="hover:bg-[#0E3056] hover:text-white transition-colors"
              >
                Schedule Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-16 bg-[#0E3056] text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">
            Measurable Results
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {metrics.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-[#FFC000] mb-2">{metric.value}</div>
                <p className="text-white font-medium mb-1">{metric.label}</p>
                <p className="text-gray-300 text-sm">{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Claims Workflow */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Streamlined Claims Workflow
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            From claim creation to payment posting, every step is optimized
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {claimsWorkflow.map((item, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg relative"
              >
                <div className="absolute -top-3 -left-3 bg-[#0E3056] dark:bg-[#FFC000] text-white dark:text-gray-900 w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {item.description}
                </p>
                <ul className="space-y-2">
                  {item.features.map((feature, idx) => (
                    <li key={idx} className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Powerful Features
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            Everything you need to manage claims efficiently
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Addressed */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Problems We Solve
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            Addressing the real challenges of claims management
          </p>

          <div className="max-w-4xl mx-auto space-y-6">
            {painPoints.map((item, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg flex flex-col md:flex-row gap-6"
              >
                <div className="md:w-1/3">
                  <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-1">
                    {item.problem}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    {item.impact}
                  </p>
                </div>
                <div className="md:w-2/3 md:border-l md:border-gray-200 md:dark:border-gray-600 md:pl-6">
                  <h4 className="text-sm font-medium text-green-600 dark:text-green-400 mb-1">
                    VIDEXA SOLUTION
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300">
                    {item.solution}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white">
              Seamless Integration
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
              Works with your existing systems
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {['Epic', 'Cerner', 'MEDITECH', 'Allscripts', 'athenahealth', 'NextGen', 'eClinicalWorks'].map((system, index) => (
                <span
                  key={index}
                  className="bg-white dark:bg-gray-800 px-6 py-3 rounded-lg shadow-sm text-gray-700 dark:text-gray-300 font-medium"
                >
                  {system}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <BlueCallToAction
        heading="Ready to streamline your claims process?"
        description="See how Videxa can help you submit clean claims and get paid faster."
        primaryButtonText="Start Free Trial"
        primaryButtonLink="/cdi-trial"
        secondaryButtonText="Schedule Demo"
        secondaryButtonLink="/contact"
      />
    </div>
  );
};

export default ClaimsManagementPage;
